import { useEffect } from 'react';

/**
 * Opens a modal directly below the customer identity card instead of over the middle of the screen,
 * so the customer being edited stays readable while the modal is up.
 *
 * The modal and the mask are portalled to document.body, so neither can be positioned from the
 * page's own CSS and neither knows where the card is. The card's bottom edge is measured here and
 * published as a custom property on the document element, which the dark modal stylesheet reads for
 * both. A body class carries the state to the mask, which takes no wrapClassName of its own.
 *
 * Re-measured on scroll and resize because the offset is in viewport coordinates.
 *
 * UNDER_CARD_LEFT_VAR anchors the modal's WRAP as well as the mask. Both start at the sider's
 * right edge, so the modal can never be centred over the nav however narrow the window gets -
 * measuring it loosely would move the dialog, not just the edge of a scrim. PLA-8844.
 */
export const UNDER_CARD_VAR = '--dk-under-card-top';
export const UNDER_CARD_LEFT_VAR = '--dk-under-card-left';
export const UNDER_CARD_BODY_CLASS = 'perx-modal-under-card-open';

const CARD_SELECTOR = '[data-testid="customer-identity-header"]';

/* Measured rather than hardcoded: the sider is 230 with the AI WorkSpace flag, 200 without it and
   80 collapsed, and it is sticky, so its right edge in viewport coordinates is its current width. */
const SIDER_SELECTOR = '.ant-layout-sider';

// Clear of the card without touching it; the modal keeps its own drop shadow off the card's edge.
const GAP = 18;

/* Breathing room each side of the modal inside the region right of the sider. 16 is antd's own
   gutter - its `.ant-modal` caps at calc(100vw - 32px) - so a constrained modal keeps the spacing
   it has when nothing constrains it. */
export const SIDE_GUTTER = 16;

const useModalUnderCard = (active: boolean): void => {
  useEffect(() => {
    if (!active) {
      return undefined;
    }

    const root = document.documentElement;

    const measure = () => {
      const card = document.querySelector(CARD_SELECTOR);

      if (!card) {
        // No card on this page: leave the modal where antd puts it rather than guessing an offset.
        root.style.removeProperty(UNDER_CARD_VAR);
        root.style.removeProperty(UNDER_CARD_LEFT_VAR);
        document.body.classList.remove(UNDER_CARD_BODY_CLASS);
        return;
      }

      const bottom = Math.max(0, Math.round(card.getBoundingClientRect().bottom + GAP));
      const sider = document.querySelector(SIDER_SELECTOR);
      const left = sider ? Math.max(0, Math.round(sider.getBoundingClientRect().right)) : 0;

      root.style.setProperty(UNDER_CARD_VAR, `${bottom}px`);
      root.style.setProperty(UNDER_CARD_LEFT_VAR, `${left}px`);
      document.body.classList.add(UNDER_CARD_BODY_CLASS);
    };

    measure();

    window.addEventListener('scroll', measure, true);
    window.addEventListener('resize', measure);

    /* Collapsing the sider fires neither scroll nor resize, so the backdrop would keep the old edge
       and re-cover the nav while a modal is open. */
    const sider = document.querySelector(SIDER_SELECTOR);
    const observer =
      sider && typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;

    observer?.observe(sider as Element);

    return () => {
      window.removeEventListener('scroll', measure, true);
      window.removeEventListener('resize', measure);
      observer?.disconnect();
      root.style.removeProperty(UNDER_CARD_VAR);
      root.style.removeProperty(UNDER_CARD_LEFT_VAR);
      document.body.classList.remove(UNDER_CARD_BODY_CLASS);
    };
  }, [active]);
};

export default useModalUnderCard;
